import type { WorkspaceKey } from '@festina-life/api'
import { notEmpty } from '@festina-life/core'
import { useMemo } from 'react'

import { useLayoutContext } from '../../../../context/layoutContext'
import { useMetaModelContext } from '../../../../context/metaModelContext'
import { useFetchBranchChangesets } from '../../../../uiModels/api/changesets/useFetchBranchChangesets'
import { useFetchWorkspaceBaseBranchChangesets } from '../../../../uiModels/api/changesets/useFetchWorkspaceBaseBranchChangesets'
import { constructIndexedTemplateOperations } from '../../../../uiModels/templateOperations/helpers'
import { isWorkspaceTemplateOperation } from '../../../../uiModels/templateOperations/types'
import { useWorkspaceOperations } from '../../../../uiModels/workspace/useWorkspaceOperations'
import { useShadowedTemplateOperations } from '../../../../workspace/useShadowedTemplateOperations'

export const useDetectRebaseWorkspaceConflicts = ({
  forWorkspace,
  toBranch,
}: {
  forWorkspace: WorkspaceKey
  toBranch: string
}) => {
  const {
    preferences: {
      parameters: { isShowingExtrinsicParameterValues },
    },
  } = useLayoutContext()

  const { blockTopology } = useMetaModelContext()

  const { data: allChangesetsOnBranchTip } = useFetchBranchChangesets({
    enabled: true,
    branchKey: { name: toBranch, server: forWorkspace.branchKey.server },
    includeBaseChangesets: true,
    shouldIncludeExtrinsicParameterValues: isShowingExtrinsicParameterValues,
  })
  const { data: allChangesetsOnWorkspaceTip } =
    useFetchWorkspaceBaseBranchChangesets(forWorkspace)
  const changesetKeysInWorkspaceTip = useMemo(
    () =>
      (allChangesetsOnWorkspaceTip?.branchChangesets ?? [])
        .filter(notEmpty)
        .map((branchChangeset) => branchChangeset.changeset?.changesetKey),
    [allChangesetsOnWorkspaceTip],
  )

  const newChangesetsOnRebase = useMemo(
    () =>
      allChangesetsOnBranchTip?.branchChangesets
        ?.filter(notEmpty)
        .filter(
          (branchChangeset) =>
            !changesetKeysInWorkspaceTip.includes(
              branchChangeset.changeset?.changesetKey,
            ),
        ),
    [allChangesetsOnBranchTip, changesetKeysInWorkspaceTip],
  )

  const {
    data: draftTemplateOperations,
    filteredLayers: filteredTemplateLayers,
  } = useWorkspaceOperations(forWorkspace, isWorkspaceTemplateOperation)

  const indexedOperationsOnRebaseDelta = useMemo(
    () =>
      constructIndexedTemplateOperations({
        configurationChangesets: newChangesetsOnRebase ?? [],
        draftTemplateOperations,
        draftAnnulOperations: [], // Draft annulments are not relevant for conflict detection since they don't resolve conflicts caused by workspace operations.
        blockTopology,
      }),
    [draftTemplateOperations, newChangesetsOnRebase, blockTopology],
  )

  return useShadowedTemplateOperations({
    templateOperations: indexedOperationsOnRebaseDelta,
    layers: filteredTemplateLayers,
    operationCondition: (operation) => operation.isDraft,
  })
}
